import { enableMapSet } from 'immer';
import { immer } from 'zustand/middleware/immer';
import { create } from 'zustand/react';
import { FolioInboxModel } from '../../activities/folio/inbox/types/types';
import { getAxios } from '../../../lib/axios';
import { FolioEndpoints } from '../../../constants/endpoints/folioEndpoints';
import ActivityStatus from '../../../enums/activities/ActivityStatus';
import { HotelBookingType } from '../../../globalModels/common/hotelBookingType';

export enum FolioTab {
  Inbox = 0,
  CheckedOut = 1,
  PreRec = 2,
  IssueRec = 3,
  Groups = 4
}

export type FolioItems = 'inbox' | 'checkedOut' | 'preRec' | 'issueRec' | 'groups';

export const isInboxFolioTarget = (target: FolioItems) => target === 'inbox' || target === 'groups';

enableMapSet();

export type FolioFilters = {
  assignedToMe?: boolean;
  unassigned?: boolean;
  currentUserId?: string;
  hotelId?: number;
  cancelled?: boolean;
};
export type FolioSort = {
  assigned?: 'desc' | 'asc';
  checkOut?: 'desc' | 'asc';
  hotel?: 'desc' | 'asc';
  receivedDate?: 'desc' | 'asc';
  company?: 'desc' | 'asc';
};

export type FolioItem = {
  value: FolioInboxModel;
  selected?: boolean;
};

export type FolioCounts = {
  Inbox: number;
  CheckedOut: number;
  PreRec: number;
  IssueRec: number;
};

export type FolioItemStore = {
  activityMap: Map<number, FolioItem>;
  inbox: number[];
  preRec: number[];
  checkedOut: number[];
  issueRec: number[];
  groups: number[];
  isUpdating: boolean;
  inboxLoading: boolean;
  checkedOutLoading: boolean;
  preRecLoading: boolean;
  issueRecLoading: boolean;
  groupsLoading: boolean;
  inboxInitialized: boolean;
  checkedOutInitialized: boolean;
  preRecInitialized: boolean;
  issueRecInitialized: boolean;
  groupsInitialized: boolean;
  currentSort: Record<FolioTab, FolioSort | null>;
  currentFilter: Record<FolioTab, FolioFilters | null>;
  setIsUpdating: (value: boolean) => void;
  init: (target: FolioTab) => Promise<void>;
  handleBulkUpdate: (items: FolioInboxModel[]) => void;
  handleBulkRemove: (items: number[]) => void;
  //selection
  selectedIds: number[];
  lastSelectedId: number | null;
  toggleSelection: (id: number) => void;
  selectSingle: (id: number) => void;
  selectRange: (startId: number | undefined, endId: number, tab: FolioItems) => void;
  clearSelection: () => void;
  //sorting
  setSort: (tab: FolioTab, sort: FolioSort | null) => void;
  getSortedIds: (tab: FolioTab) => number[];
  setFilter: (tab: FolioTab, filters: FolioFilters | null) => void;
};

// Determines which tab an item belongs to based on its Status
function getTabForItem(item: FolioInboxModel): FolioTab | null {
  const isGroupBooking = item.BookingType === HotelBookingType.Group;

  switch (item.Status) {
    case ActivityStatus.Folio:
      return isGroupBooking ? FolioTab.Groups : FolioTab.Inbox;
    case ActivityStatus.CheckedOut:
      return isGroupBooking ? null : FolioTab.CheckedOut;
    case ActivityStatus.PreReconciliation:
      return isGroupBooking ? null : FolioTab.PreRec;
    case ActivityStatus.IssueReconciliation:
      return isGroupBooking ? null : FolioTab.IssueRec;
    default:
      return null; // Item doesn't belong to any folio tab
  }
}

// Comparison functions for sorting
const assignedDesc = (itemA: FolioInboxModel, itemB: FolioInboxModel) => {
  const a = itemA.AssignedUser;
  const b = itemB.AssignedUser;
  if (a == null && b == null) return 0; // both missing
  if (a == null) return 1; // put undefined/null last
  if (b == null) return -1;
  return a.localeCompare(b);
};

const assignedAsc = (itemA: FolioInboxModel, itemB: FolioInboxModel) => {
  const a = itemA.AssignedUser;
  const b = itemB.AssignedUser;
  if (a == null && b == null) return 0; // both missing
  if (a == null) return -1; // put undefined/null first
  if (b == null) return 1;
  return b.localeCompare(a);
};

const HotelCheckoutAsc = (a: FolioInboxModel, b: FolioInboxModel) => {
  const dateA = a.EndDate ? new Date(a.EndDate) : null;
  const dateB = b.EndDate ? new Date(b.EndDate) : null;
  const nameA = a.HotelName ?? '';
  const nameB = b.HotelName ?? '';
  if (!dateA && !dateB) return nameA.localeCompare(nameB); // both missing
  if (!dateA) return 1; // put undefined/null last
  if (!dateB) return -1;
  if (dateA.getTime() !== dateB.getTime()) {
    return dateA.getTime() - dateB.getTime();
  }
  return nameA.localeCompare(nameB);
};

const HotelCheckoutDesc = (a: FolioInboxModel, b: FolioInboxModel) => {
  const dateA = a.EndDate ? new Date(a.EndDate) : null;
  const dateB = b.EndDate ? new Date(b.EndDate) : null;
  const nameA = a.HotelName ?? '';
  const nameB = b.HotelName ?? '';
  if (!dateA && !dateB) return nameA.localeCompare(nameB); // both missing
  if (!dateA) return 1; // put undefined/null last
  if (!dateB) return -1;
  if (dateA.getTime() !== dateB.getTime()) {
    return dateB.getTime() - dateA.getTime(); // reversed for descending
  }
  return nameA.localeCompare(nameB);
};

const HotelAsc = (a: FolioInboxModel, b: FolioInboxModel) => {
  const nameA = a.HotelName ?? '';
  const nameB = b.HotelName ?? '';
  if (!nameA && !nameB) return 0; // both missing
  if (!nameA) return 1; // put undefined/null last
  if (!nameB) return -1;
  return nameA.localeCompare(nameB); // A-Z
};

const HotelDesc = (a: FolioInboxModel, b: FolioInboxModel) => {
  const nameA = a.HotelName ?? '';
  const nameB = b.HotelName ?? '';
  if (!nameA && !nameB) return 0; // both missing
  if (!nameA) return 1; // put undefined/null last
  if (!nameB) return -1;
  return nameB.localeCompare(nameA); // Z-A
};

const CompanyAsc = (a: FolioInboxModel, b: FolioInboxModel) => {
  const nameA = a.Company ?? '';
  const nameB = b.Company ?? '';
  if (!nameA && !nameB) return 0; // both missing
  if (!nameA) return 1; // put undefined/null last
  if (!nameB) return -1;
  return nameA.localeCompare(nameB); // A-Z
};

const CompanyDesc = (a: FolioInboxModel, b: FolioInboxModel) => {
  const nameA = a.Company ?? '';
  const nameB = b.Company ?? '';
  if (!nameA && !nameB) return 0; // both missing
  if (!nameA) return 1; // put undefined/null last
  if (!nameB) return -1;
  return nameB.localeCompare(nameA); // Z-A
};

const ReceivedDateAsc = (a: FolioInboxModel, b: FolioInboxModel) => {
  const dateA = a.ReceivedDate ? new Date(a.ReceivedDate) : null;
  const dateB = b.ReceivedDate ? new Date(b.ReceivedDate) : null;
  if (!dateA && !dateB) return 0; // both missing
  if (!dateA) return 1; // put undefined/null last
  if (!dateB) return -1;
  return dateA.getTime() - dateB.getTime();
};

const ReceivedDateDesc = (a: FolioInboxModel, b: FolioInboxModel) => {
  const dateA = a.ReceivedDate ? new Date(a.ReceivedDate) : null;
  const dateB = b.ReceivedDate ? new Date(b.ReceivedDate) : null;
  if (!dateA && !dateB) return 0; // both missing
  if (!dateA) return 1; // put undefined/null last
  if (!dateB) return -1;
  return dateB.getTime() - dateA.getTime(); // reversed for descending
};

const getSortCompareFn = (sort: FolioSort): ((a: FolioInboxModel, b: FolioInboxModel) => number) => {
  // Build array of active comparison functions in priority order
  const comparators: Array<(a: FolioInboxModel, b: FolioInboxModel) => number> = [];

  // Priority 1: Assigned
  if (sort.assigned) {
    comparators.push(sort.assigned === 'asc' ? assignedAsc : assignedDesc);
  }

  // Priority 2: Check Out
  if (sort.checkOut) {
    comparators.push(sort.checkOut === 'asc' ? HotelCheckoutAsc : HotelCheckoutDesc);
  }

  // Priority 3: Received Date
  if (sort.receivedDate) {
    comparators.push(sort.receivedDate === 'asc' ? ReceivedDateAsc : ReceivedDateDesc);
  }

  // Priority 4: Hotel
  if (sort.hotel) {
    comparators.push(sort.hotel === 'asc' ? HotelAsc : HotelDesc);
  }

  if (sort.company) {
    comparators.push(sort.company === 'asc' ? CompanyAsc : CompanyDesc);
  }

  // Return composite comparator that chains all active sorts
  return (a: FolioInboxModel, b: FolioInboxModel) => {
    for (const comparator of comparators) {
      const result = comparator(a, b);
      if (result !== 0) return result; // Found difference, return it
    }
    return 0; // All comparisons equal
  };
};

export const useFolioItemStore = create<FolioItemStore>()(
  immer((set, get) => ({
    activityMap: new Map(),
    preRec: [],
    inbox: [],
    checkedOut: [],
    issueRec: [],
    groups: [],

    isUpdating: false,
    inboxLoading: false,
    checkedOutLoading: false,
    preRecLoading: false,
    issueRecLoading: false,
    groupsLoading: false,
    inboxInitialized: false,
    checkedOutInitialized: false,
    preRecInitialized: false,
    issueRecInitialized: false,
    groupsInitialized: false,
    currentFilter: {
      [FolioTab.Inbox]: null,
      [FolioTab.CheckedOut]: null,
      [FolioTab.PreRec]: null,
      [FolioTab.IssueRec]: null,
      [FolioTab.Groups]: null
    },
    currentSort: {
      [FolioTab.Inbox]: { receivedDate: 'desc' },
      [FolioTab.CheckedOut]: { checkOut: 'asc' },
      [FolioTab.PreRec]: { checkOut: 'asc' },
      [FolioTab.IssueRec]: { checkOut: 'asc' },
      [FolioTab.Groups]: { receivedDate: 'desc' }
    },
    setIsUpdating: (value: boolean) =>
      set((state) => {
        state.isUpdating = value;
      }),
    init: async (target) => {
      // Clear selection when switching tabs
      get().clearSelection();

      // Check if already initialized or loading to prevent duplicate calls
      const state = get();
      if (target === FolioTab.Inbox && (state.inboxInitialized || state.inboxLoading)) return;
      if (target === FolioTab.CheckedOut && (state.checkedOutInitialized || state.checkedOutLoading)) return;
      if (target === FolioTab.PreRec && (state.preRecInitialized || state.preRecLoading)) return;
      if (target === FolioTab.IssueRec && (state.issueRecInitialized || state.issueRecLoading)) return;
      if (target === FolioTab.Groups && (state.groupsInitialized || state.groupsLoading)) return;

      // Set loading state for this specific tab
      set((state) => {
        if (target === FolioTab.Inbox) state.inboxLoading = true;
        if (target === FolioTab.CheckedOut) state.checkedOutLoading = true;
        if (target === FolioTab.PreRec) state.preRecLoading = true;
        if (target === FolioTab.IssueRec) state.issueRecLoading = true;
        if (target === FolioTab.Groups) state.groupsLoading = true;
      });

      try {
        const response = await getAxios().get(`${FolioEndpoints.folioInbox.items}?tab=${target}`);

        set((state) => {
          const activities: FolioInboxModel[] = response.data;
          activities.forEach((activity) => {
            state.activityMap.set(activity.Id, { value: activity });
            if (target === FolioTab.Inbox) {
              state.inbox.push(activity.Id);
            } else if (target === FolioTab.CheckedOut) {
              state.checkedOut.push(activity.Id);
            } else if (target === FolioTab.PreRec) {
              state.preRec.push(activity.Id);
            } else if (target === FolioTab.IssueRec) {
              state.issueRec.push(activity.Id);
            } else if (target === FolioTab.Groups) {
              state.groups.push(activity.Id);
            }
          });

          // Apply current sort if one exists
          const currentSort = state.currentSort[target];
          if (currentSort) {
            const getItem = (id: number) => state.activityMap.get(id)!.value;
            const compareFn = getSortCompareFn(currentSort);

            if (target === FolioTab.Inbox) {
              state.inbox.sort((a, b) => compareFn(getItem(a), getItem(b)));
            } else if (target === FolioTab.CheckedOut) {
              state.checkedOut.sort((a, b) => compareFn(getItem(a), getItem(b)));
            } else if (target === FolioTab.PreRec) {
              state.preRec.sort((a, b) => compareFn(getItem(a), getItem(b)));
            } else if (target === FolioTab.IssueRec) {
              state.issueRec.sort((a, b) => compareFn(getItem(a), getItem(b)));
            } else if (target === FolioTab.Groups) {
              state.groups.sort((a, b) => compareFn(getItem(a), getItem(b)));
            }
          }

          // Mark as initialized and stop loading
          if (target === FolioTab.Inbox) {
            state.inboxInitialized = true;
            state.inboxLoading = false;
          } else if (target === FolioTab.CheckedOut) {
            state.checkedOutInitialized = true;
            state.checkedOutLoading = false;
          } else if (target === FolioTab.PreRec) {
            state.preRecInitialized = true;
            state.preRecLoading = false;
          } else if (target === FolioTab.IssueRec) {
            state.issueRecInitialized = true;
            state.issueRecLoading = false;
          } else if (target === FolioTab.Groups) {
            state.groupsInitialized = true;
            state.groupsLoading = false;
          }
        });
      } catch (error) {
        // Stop loading on error
        set((state) => {
          if (target === FolioTab.Inbox) state.inboxLoading = false;
          if (target === FolioTab.CheckedOut) state.checkedOutLoading = false;
          if (target === FolioTab.PreRec) state.preRecLoading = false;
          if (target === FolioTab.IssueRec) state.issueRecLoading = false;
          if (target === FolioTab.Groups) state.groupsLoading = false;
        });
        throw error;
      }
    },
    selectedIds: [],
    lastSelectedId: null,
    toggleSelection: (id) =>
      set((state) => {
        const index = state.selectedIds.indexOf(id);
        const item = state.activityMap.get(id);
        if (index > -1) {
          state.selectedIds.splice(index, 1);
          if (item) {
            state.activityMap.set(id, { ...item, selected: false });
          }
        } else {
          state.selectedIds.push(id);
          if (item) {
            state.activityMap.set(id, { ...item, selected: true });
          }
        }
        state.lastSelectedId = id;
      }),
    selectSingle: (id) =>
      set((state) => {
        for (const [itemId, item] of state.activityMap) {
          if (item.selected) {
            state.activityMap.set(itemId, { ...item, selected: false });
          }
        }

        // Select only the clicked item
        const item = state.activityMap.get(id);
        if (item) {
          state.activityMap.set(id, { ...item, selected: true });
          state.selectedIds = [id];
          state.lastSelectedId = id;
        }
      }),
    selectRange: (startId, endId, tab) =>
      set((state) => {
        const items = state[tab];
        const endIndex = items.indexOf(endId);
        if (endIndex === -1) return;

        const startIndex = startId != null ? items.indexOf(startId) : -1;
        let rangeIds: number[];

        if (startIndex === -1) {
          // No anchor to range from, fall back to a single selection
          rangeIds = [endId];
          state.lastSelectedId = endId;
        } else {
          const minIndex = Math.min(startIndex, endIndex);
          const maxIndex = Math.max(startIndex, endIndex);
          rangeIds = items.slice(minIndex, maxIndex + 1);
        }

        const selectionSet = new Set(rangeIds);

        for (const [itemId, item] of state.activityMap) {
          const shouldSelect = selectionSet.has(itemId);
          if (!!item.selected !== shouldSelect) {
            state.activityMap.set(itemId, { ...item, selected: shouldSelect });
          }
        }

        state.selectedIds = rangeIds;
      }),
    clearSelection: () =>
      set((state) => {
        for (const [id, item] of state.activityMap) {
          if (item.selected) {
            state.activityMap.set(id, { ...item, selected: false });
          }
        }
        state.lastSelectedId = null;
        state.selectedIds = [];
      }),
    handleBulkUpdate: (items) => {
      set((state) => {
        // Force new Map to bypass Immer proxy issues
        const newMap = new Map(state.activityMap);
        const getItem = (id: number) => newMap.get(id)!.value;

        // Helper: Check if item passes filter for a tab
        const passesFilter = (item: FolioInboxModel, tab: FolioTab) => {
          const filter = state.currentFilter[tab];
          if (!filter) return true; // No filter, always pass

          const assignedUser = item.AssignedUser?.trim();
          const isAssigned = assignedUser && assignedUser.length > 0;

          if (!filter.assignedToMe && !filter.unassigned) return true;

          if (filter.assignedToMe) {
            const matchesMe = item.AssignedUserId === filter.currentUserId;
            if (filter.unassigned) {
              return matchesMe || !isAssigned;
            }
            return matchesMe;
          }

          if (filter.unassigned) {
            return !isAssigned;
          }

          return true;
        };

        items.forEach((item) => {
          const existingItem = newMap.get(item.Id);

          // Update the item in the map
          newMap.set(item.Id, {
            value: { ...item }, // Force new object reference
            selected: existingItem?.selected
          });

          // Determine which tab this item should belong to based on its Status
          const correctTab = getTabForItem(item);

          // Remove item from ALL tab arrays first (to handle cross-tab moves)
          state.inbox = state.inbox.filter((id) => id !== item.Id);
          state.checkedOut = state.checkedOut.filter((id) => id !== item.Id);
          state.preRec = state.preRec.filter((id) => id !== item.Id);
          state.issueRec = state.issueRec.filter((id) => id !== item.Id);
          state.groups = state.groups.filter((id) => id !== item.Id);

          // If item belongs to a folio tab, add it to the correct array (if it passes filter)
          if (correctTab !== null) {
            if (passesFilter(item, correctTab)) {
              // Get the correct array and sort config for this tab
              let targetArray: number[];
              let sortConfig: FolioSort | null;

              switch (correctTab) {
                case FolioTab.Inbox:
                  targetArray = state.inbox;
                  sortConfig = state.currentSort[FolioTab.Inbox];
                  break;
                case FolioTab.CheckedOut:
                  targetArray = state.checkedOut;
                  sortConfig = state.currentSort[FolioTab.CheckedOut];
                  break;
                case FolioTab.PreRec:
                  targetArray = state.preRec;
                  sortConfig = state.currentSort[FolioTab.PreRec];
                  break;
                case FolioTab.IssueRec:
                  targetArray = state.issueRec;
                  sortConfig = state.currentSort[FolioTab.IssueRec];
                  break;
                case FolioTab.Groups:
                  targetArray = state.groups;
                  sortConfig = state.currentSort[FolioTab.Groups];
                  break;
              }

              // Add to target array with proper sorting
              if (sortConfig) {
                const compareFn = getSortCompareFn(sortConfig);
                const updatedArray = updatedSortedList(targetArray, item.Id, item, compareFn, getItem);

                // Update the correct state array
                switch (correctTab) {
                  case FolioTab.Inbox:
                    state.inbox = updatedArray;
                    break;
                  case FolioTab.CheckedOut:
                    state.checkedOut = updatedArray;
                    break;
                  case FolioTab.PreRec:
                    state.preRec = updatedArray;
                    break;
                  case FolioTab.IssueRec:
                    state.issueRec = updatedArray;
                    break;
                  case FolioTab.Groups:
                    state.groups = updatedArray;
                    break;
                }
              } else {
                // No sorting, just append to the end
                switch (correctTab) {
                  case FolioTab.Inbox:
                    state.inbox.push(item.Id);
                    break;
                  case FolioTab.CheckedOut:
                    state.checkedOut.push(item.Id);
                    break;
                  case FolioTab.PreRec:
                    state.preRec.push(item.Id);
                    break;
                  case FolioTab.IssueRec:
                    state.issueRec.push(item.Id);
                    break;
                  case FolioTab.Groups:
                    state.groups.push(item.Id);
                    break;
                }
              }
            }
          }
        });

        state.activityMap = newMap;
        state.isUpdating = false;
      });
    },
    handleBulkRemove: (items) => {
      set((state) => {
        items.forEach((id) => {
          state.activityMap.delete(id);
        });

        // Remove from all tab arrays
        state.inbox = state.inbox.filter((id) => !items.includes(id));
        state.checkedOut = state.checkedOut.filter((id) => !items.includes(id));
        state.preRec = state.preRec.filter((id) => !items.includes(id));
        state.issueRec = state.issueRec.filter((id) => !items.includes(id));
        state.groups = state.groups.filter((id) => !items.includes(id));

        state.isUpdating = false;
      });
    },
    setFilter(tab, filters) {
      set((state) => {
        state.currentFilter[tab] = filters;
        const allIds = Array.from(state.activityMap.keys());
        const doesItemPass = (id: number) => {
          const item = state.activityMap.get(id)?.value;
          if (!item) return false;

          const correctTab = getTabForItem(item);
          if (correctTab !== tab) return false;
          if (!filters) return true;
          const assignedUser = item.AssignedUser?.trim();
          const isAssigned = assignedUser && assignedUser.length > 0;
          if (!filters.assignedToMe && !filters.unassigned && !filters.cancelled) {
            return true;
          }
          if (filters.assignedToMe) {
            const matchesMe = item.AssignedUserId === filters.currentUserId;
            if (filters.unassigned) {
              return matchesMe || !isAssigned;
            }
            return matchesMe;
          }
          if (filters.unassigned) {
            return !isAssigned;
          }
          if (filters.cancelled) {
            return item.IsCancelled === true;
          }

          return true;
        };

        const filteredIds = allIds.filter(doesItemPass);

        const currentSort = state.currentSort[tab];
        if (currentSort) {
          const getItem = (id: number) => state.activityMap.get(id)!.value;
          const compareFn = getSortCompareFn(currentSort);
          filteredIds.sort((a, b) => compareFn(getItem(a), getItem(b)));
        }
        switch (tab) {
          case FolioTab.Inbox:
            state.inbox = filteredIds;
            break;
          case FolioTab.CheckedOut:
            state.checkedOut = filteredIds;
            break;
          case FolioTab.PreRec:
            state.preRec = filteredIds;
            break;
          case FolioTab.IssueRec:
            state.issueRec = filteredIds;
            break;
          case FolioTab.Groups:
            state.groups = filteredIds;
            break;
        }
      });
    },
    setSort: (tab, sort) => {
      set((state) => {
        state.currentSort[tab] = sort;
        const getItem = (id: number) => state.activityMap.get(id)!.value;

        if (sort) {
          //TODO make better
          const compareFn = getSortCompareFn(sort);
          if (tab === FolioTab.Inbox) {
            state.inbox.sort((a, b) => compareFn(getItem(a), getItem(b)));
          } else if (tab === FolioTab.CheckedOut) {
            state.checkedOut.sort((a, b) => compareFn(getItem(a), getItem(b)));
          } else if (tab === FolioTab.PreRec) {
            state.preRec.sort((a, b) => compareFn(getItem(a), getItem(b)));
          } else if (tab === FolioTab.IssueRec) {
            state.issueRec.sort((a, b) => compareFn(getItem(a), getItem(b)));
          } else if (tab === FolioTab.Groups) {
            state.groups.sort((a, b) => compareFn(getItem(a), getItem(b)));
          }
        }
        // If sort is null, keep current order (no re-sorting)
      });
    },
    getSortedIds: (tab) => {
      const state = get();
      switch (tab) {
        case FolioTab.Inbox:
          return state.inbox;
        case FolioTab.CheckedOut:
          return state.checkedOut;
        case FolioTab.PreRec:
          return state.preRec;
        case FolioTab.IssueRec:
          return state.issueRec;
        case FolioTab.Groups:
          return state.groups;
        default:
          return [];
      }
    }
  }))
);

function updatedSortedList(
  list: number[],
  id: number,
  item: FolioInboxModel,
  compareFn: (a: FolioInboxModel, b: FolioInboxModel) => number,
  getItem: (id: number) => FolioInboxModel
) {
  const oldIndex = list.indexOf(id);
  const newList = [...list];
  if (oldIndex !== -1) {
    newList.splice(oldIndex, 1);
  }

  const insertIndex = binarySearch(newList, item, compareFn, getItem);

  newList.splice(insertIndex, 0, id);
  return newList;
}

function binarySearch(
  list: number[],
  item: FolioInboxModel,
  compareFn: (a: FolioInboxModel, b: FolioInboxModel) => number,
  getItem: (id: number) => FolioInboxModel
) {
  let low = 0;
  let high = list.length;
  while (low < high) {
    const mid = (low + high) >>> 1; //forced int is dived by 2
    const cmp = compareFn(item, getItem(list[mid]));
    if (cmp > 0) low = mid + 1;
    else high = mid;
  }
  return low;
}
